import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import Logo from './Logo';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const NAV = [
  { label: 'Home', to: '/' },
  { label: 'Services', to: '/services' },
  {
    label: 'Start Your Recovery',
    children: [
      { label: 'Getting Started', to: '/getting-started' },
      { label: 'Patient Portal', to: '/patient-portal' },
    ],
  },
  { label: 'Tools', to: '/tools' },
  { label: 'FAQs', to: '/faqs' },
  { label: 'About Us', to: '/about' },
  { label: 'More', children: [{ label: 'Blog', to: '/blog' }] },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (to) => location.pathname === to;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur shadow-sm' : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Logo />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((item) =>
              item.children ? (
                <div key={item.label} className="relative group">
                  <button className="flex items-center gap-1 font-heading text-[15px] text-navy/90 hover:text-navy transition-colors">
                    {item.label}
                    <ChevronDown size={15} className="mt-0.5 transition-transform group-hover:rotate-180" />
                  </button>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="bg-white rounded-lg shadow-xl border border-border py-2 min-w-[190px]">
                      {item.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          className="block px-5 py-2.5 text-sm text-navy/80 hover:bg-secondary hover:text-navy transition-colors"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`hover-underline font-heading text-[15px] transition-colors ${
                    isActive(item.to) ? 'text-navy font-medium' : 'text-navy/90 hover:text-navy'
                  }`}
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>

          <div className="hidden lg:block">
            <Link
              to="/getting-started"
              className="inline-flex items-center rounded-full bg-navy text-white px-5 py-2.5 text-sm font-heading font-medium tracking-wide hover:bg-[#2c4d76] transition-colors shadow-sm"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile trigger */}
          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button aria-label="Open menu" className="p-2 text-navy">
                  <Menu size={26} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] p-0">
                <div className="flex items-center justify-between px-5 h-20 border-b">
                  <span className="font-heading font-semibold text-navy tracking-wide">Menu</span>
                  <button onClick={() => setOpen(false)} className="p-1 text-navy"><X size={22} /></button>
                </div>
                <div className="py-3">
                  {NAV.map((item) =>
                    item.children ? (
                      <div key={item.label} className="px-5 py-2">
                        <div className="font-heading text-navy font-semibold text-[15px] mb-1">{item.label}</div>
                        {item.children.map((c) => (
                          <Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="block pl-3 py-2 text-navy/75 hover:text-navy">
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <Link key={item.to} to={item.to} onClick={() => setOpen(false)} className="block px-5 py-3 font-heading text-[15px] text-navy/90 hover:bg-secondary">
                        {item.label}
                      </Link>
                    )
                  )}
                  <div className="px-5 pt-4">
                    <Link to="/getting-started" onClick={() => setOpen(false)} className="block text-center rounded-full bg-navy text-white px-5 py-3 text-sm font-heading font-medium">
                      Get Started
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
